// TestimonialsSection.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './TestimonialsSection.css';
import apostrophySvg from '../../assets/appostrophy.svg';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useHomeTestimonials, useHomePageData } from '../../content/useSiteContent';
import { withTestimonialAudio } from './testimonialsHomeData';

const chunkTestimonials = (items, size) => {
  const result = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
};

const TestimonialsSection = ({
  sectionId = 'case-studies',
  trackSection = sectionId === 'client-testimonials' ? 'case-studies-testimonials' : 'home-testimonials',
} = {}) => {
  const sectionRef = useScrollAnimation({ threshold: 0.2 });
  const { testimonialsHeading } = useHomePageData();
  const rawItems = useHomeTestimonials();
  const slides = useMemo(
    () => chunkTestimonials(withTestimonialAudio(rawItems), 4),
    [rawItems]
  );
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideClass, setSlideClass] = useState('');
  const audioRefs = useRef({});
  const sliderTrackRef = useRef(null);

  useEffect(() => {
    if (currentSlide >= slides.length) {
      setCurrentSlide(Math.max(0, slides.length - 1));
    }
  }, [slides.length, currentSlide]);

  const scrollToSlideStart = useCallback(() => {
    requestAnimationFrame(() => {
      const target = sliderTrackRef.current ?? sectionRef.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [sectionRef]);

  const goToSlide = (index) => {
    if (index === currentSlide) return;
    setSlideClass(index > currentSlide ? 'slide-from-right' : 'slide-from-left');
    setCurrentSlide(index);
    scrollToSlideStart();
  };

  const stopAllAudio = () => {
    Object.values(audioRefs.current).forEach((el) => {
      if (!el) return;
      try {
        el.pause();
        el.currentTime = 0;
      } catch (_) {
        /* noop */
      }
    });
  };

  useEffect(() => {
    stopAllAudio();
  }, [currentSlide]);

  useEffect(() => {
    return () => stopAllAudio();
  }, []);

  const cards = slides[currentSlide] ?? [];

  return (
    <>
      <section
        className="testimonials-section"
        id={sectionId}
        ref={sectionRef}
        data-track-section={trackSection}
      >
        <div className="section-container">
          <h2 className="section-headline">
            {testimonialsHeading.headingPrefix} <span className="headline-purple">{testimonialsHeading.headingAccent}</span> {testimonialsHeading.headingSuffix}
          </h2>
          <p className="section-description">
            {testimonialsHeading.description}
          </p>

          <div className="testimonials-slider-track" ref={sliderTrackRef}>
            <div key={currentSlide} className={`testimonials-grid${slideClass ? ` ${slideClass}` : ''}`}>
              {cards.map((card) => {
                const hasAudio = Boolean(card.audio);
                return (
                  <div
                    key={card.id}
                    className="testimonial-card"
                    style={{ backgroundImage: card.bg ? `url(${card.bg})` : undefined }}
                  >
                    <img
                      src={apostrophySvg}
                      alt=""
                      className="quote-icon"
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                    />
                    <p className="testimonial-quote">{card.quote}</p>
                    {card.authorName && (
                      <div className="testimonial-author" style={{ marginTop: '1rem', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '1rem' }}>
                        <div style={{ fontWeight: '600', color: '#1a1a1a' }}>{card.authorName}</div>
                        {card.authorRole && <div style={{ fontSize: '0.85rem', color: '#666' }}>{card.authorRole}</div>}
                      </div>
                    )}
                    {hasAudio ? (
                      <audio
                        ref={(el) => {
                          if (el) audioRefs.current[card.id] = el;
                          else delete audioRefs.current[card.id];
                        }}
                        data-src={card.audio}
                        loop
                        preload="none"
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="testimonials-dots">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`dot${i === currentSlide ? ' dot--active' : ''}`}
                onClick={() => goToSlide(i)}
                role="button"
                tabIndex={0}
                aria-label={`Go to slide ${i + 1}`}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goToSlide(i)}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default TestimonialsSection;
